import { useCallback, useEffect, useRef, useState } from "react";
import {
  ViewerContainer,
  FrameImage,
  Skeleton,
  Spinner,
} from "./Spin360Viewer.styles";
import { DragHint } from "../DragHint/DragHint";

interface Spin360ViewerProps {
  frames: string[];
  loop: boolean;
  dragSensitivity: number;
  preloadAll: boolean;
  cover?: string;
  lazy?: boolean;
  preloadCount?: number;
  hint?: { show: boolean; label: string; color: string };
  ariaLabel?: string;
  resetSignal?: string | number;
  autoRotate?: boolean;
  autoRotateSpeed?: number;
  maxHeight?: { mobile: string; desktop: string };
}

export const Spin360Viewer = ({
  frames,
  loop,
  dragSensitivity,
  preloadAll,
  cover,
  lazy = false,
  preloadCount = 8,
  hint,
  ariaLabel = "Visualização 360°",
  resetSignal,
  autoRotate = false,
  autoRotateSpeed = 10,
  maxHeight = { mobile: "20rem", desktop: "28rem" },
}: Spin360ViewerProps) => {
  const total = frames.length;
  const isLazy = lazy && !!cover;
  const [activated, setActivated] = useState(!isLazy);
  const [frameIndex, setFrameIndex] = useState(0);
  const [firstFrameReady, setFirstFrameReady] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragStateRef = useRef<{ startX: number; startIndex: number } | null>(
    null,
  );
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setFrameIndex(0);
    setFirstFrameReady(false);
    setActivated(!isLazy);
  }, [resetSignal, isLazy]);

  // Preload só após ativação (lazy) ou no mount (não-lazy).
  useEffect(() => {
    if (!activated || total === 0) return;
    setFirstFrameReady(false);
    let cancelled = false;
    const targets = preloadAll
      ? frames
      : frames.slice(0, Math.max(1, preloadCount));
    let pendingFirst = true;
    targets.forEach((src, i) => {
      const img = new Image();
      const settle = () => {
        if (cancelled) return;
        if (i === 0 && pendingFirst) {
          pendingFirst = false;
          setFirstFrameReady(true);
        }
      };
      img.onload = settle;
      img.onerror = settle;
      img.src = src;
    });
    return () => {
      cancelled = true;
    };
  }, [frames, preloadAll, total, activated, preloadCount]);

  useEffect(() => {
    if (!activated || !autoRotate || isDragging || total <= 1) return;
    const msPerFrame = (autoRotateSpeed * 1000) / total;
    const interval = setInterval(() => {
      setFrameIndex((prev) => (prev + 1) % total);
    }, msPerFrame);
    return () => clearInterval(interval);
  }, [activated, autoRotate, autoRotateSpeed, isDragging, total]);

  const updateIndex = useCallback(
    (startIndex: number, delta: number) => {
      if (total === 0) return;
      const step = Math.round(delta / Math.max(1, dragSensitivity));
      if (loop) {
        const next = (((startIndex + step) % total) + total) % total;
        setFrameIndex(next);
      } else {
        const next = Math.max(0, Math.min(total - 1, startIndex + step));
        setFrameIndex(next);
      }
    },
    [dragSensitivity, loop, total],
  );

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!activated) {
      setActivated(true);
      return;
    }
    if (total <= 1) return;
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
    dragStateRef.current = { startX: e.clientX, startIndex: frameIndex };
    setIsDragging(true);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const state = dragStateRef.current;
    if (!state) return;
    updateIndex(state.startIndex, e.clientX - state.startX);
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    try {
      (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
    dragStateRef.current = null;
    setIsDragging(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!activated) {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setActivated(true);
      }
      return;
    }
    if (total <= 1) return;
    if (e.key === "ArrowRight") {
      e.preventDefault();
      updateIndex(frameIndex, dragSensitivity);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      updateIndex(frameIndex, -dragSensitivity);
    }
  };

  if (total === 0) {
    return cover ? (
      <div>
        <ViewerContainer
          $isDragging={false}
          $hMobile={maxHeight.mobile}
          $hDesktop={maxHeight.desktop}
        >
          <FrameImage src={cover} alt="" draggable={false} $visible />
        </ViewerContainer>
      </div>
    ) : null;
  }

  const showCover = !!cover && (!activated || !firstFrameReady);
  const displaySrc = showCover ? (cover as string) : frames[frameIndex];
  const imageVisible = showCover || firstFrameReady;

  return (
    <div>
      <ViewerContainer
        ref={containerRef}
        role="img"
        aria-label={ariaLabel}
        tabIndex={0}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={handleKeyDown}
        $isDragging={isDragging}
        $hMobile={maxHeight.mobile}
        $hDesktop={maxHeight.desktop}
      >
        <FrameImage src={displaySrc} alt="" draggable={false} $visible={imageVisible} />
        {!cover && !firstFrameReady && <Skeleton aria-hidden />}
        {cover && activated && !firstFrameReady && <Spinner aria-hidden />}
      </ViewerContainer>
      {hint?.show && total > 1 && (
        <DragHint
          label={hint.label}
          color={hint.color}
        />
      )}
    </div>
  );
};
